import { useState, useEffect, useRef, useMemo, Children } from 'react';
import {
  Box,
  Container,
  useTheme,
  useMediaQuery,
  Drawer,
  Dialog,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Popover,
  Chip,
  ListItemButton,
  Button,
  Card as MuiCard,
  CardContent,
  Stack,
  Avatar as MuiAvatar,
  SvgIcon,
  LinearProgress,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TextField,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { MuiTelInput, matchIsValidTel } from 'mui-tel-input';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faUsers,
  faClock,
  faPlane,
  faBriefcase,
  faChartLine,
  faWallet,
  faFileAlt,
  faChartBar,
  faCog,
  faBell,
  faSearch,
  faChevronRight,
  faRobot,
  faPaperPlane,
  faTimes,
  faPlus,
  faCheck,
  faBuilding,
  faAward,
  faSignOutAlt,
  faBook,
  faShieldAlt,
  faSparkles,
  faArrowRight,
  faArrowLeft,
  faDownload,
  faUser,
  faStar,
  faMapPin,
  faEnvelope,
  faPhone,
  faPencil,
  faAlertTriangle,
  faHashtag,
  faCalendar,
  faGraduationCap,
  faUserPlus,
  faComment,
  faBars,
} from '@fortawesome/free-solid-svg-icons';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import {
  Wallet,
  Users,
  ShieldCheck,
  Plus,
  Check,
  ArrowLeft,
  ArrowRight,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Building2,
  GraduationCap,
  Clock,
  X,
  CalendarOff,
  CircleUser,
  Sparkles,
  TrendingUp,
  TrendingDown,
  LayoutDashboard,
  FileText,
  BarChart3,
  Home,
  BookOpen,
  UserPlus,
  AlertTriangle,
  Settings,
  Search,
  Bell,
  Send,
  Hash,
  Calendar,
  Pencil,
  CornerDownLeft,
  ChevronRight,
  MessageSquare,
  LogOut,
  Bot,
  Download,
  Star,
  PanelLeft,
  UserCog,
  SlidersHorizontal,
  Award,
  Menu as MenuIcon,
} from 'lucide-react';
import { api } from './api';
import EmployeesDataGrid from './EmployeesDataGrid';
import AppDataGrid, {
  DataGridCard,
  DataGridPage,
  PersonCell,
  TextCell,
  GridStatusPill,
  useAttendanceColumns,
  useHistoryColumns,
} from './AppDataGrid';
import { useAuth } from './auth';
import { useAppSettings } from './SettingsContext';
import { buildPath, parsePath } from './routing';
import {
  StatusPill,
  UserAvatar,
  Header,
  SectionHeader,
  GridLayout,
  EmptyState,
} from './components';
import { FONT, AG, PIE_COLORS, STATUS_COLOR, statusTone } from './theme/tokens';
import { initials, empEmail, tenure, fmtPKR, fmtM, greeting } from './lib/format';
import { STAGES, JOB_STAGES, jobTone, jobName } from './lib/ats';
import { printPayDoc, docRow, printReport } from './lib/print';
import { OKR_RATINGS, RATING_NUM, ratingTone, okrStatusTone, okrTabBtn, okrLbl, weightedProgress, avgRatingLabel, okrsByEmployee } from './lib/okr';
import { Pill, Avatar, AccountButton, Card, DashboardWidget, WidgetGrid, KPI, CardSectionHeader, ChartCard, SectionTitle, ChartTip, CenterSpinner, PrimaryBtn, OutlineBtn, PageScroll, PTh, PTd, PerfTable, DrawerFormRow, DrawerSection, DrawerTextField, DRAWER_TABLE_SX } from './ui';

function ChatWidget({ open, setOpen, balances, seed, clearSeed }) {
  const [msgs, setMsgs] = useState([
    {
      from: 'bot',
      text: "Hi! I'm your Augment HR assistant. Ask me about your leave balance, policies, payslips, or any HR process.",
    },
  ]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const endRef = useRef(null);
  // Draggable FAB — null = default bottom-right; resets on page refresh.
  const [fabPos, setFabPos] = useState(null);
  const dragState = useRef(null);
  const onFabPointerDown = (e) => {
    e.preventDefault();
    const btnSize = 56;
    const startX = fabPos ? fabPos.x : window.innerWidth - 24 - btnSize;
    const startY = fabPos ? fabPos.y : window.innerHeight - 24 - btnSize;
    dragState.current = { moved: false, ox: e.clientX - startX, oy: e.clientY - startY };
    const onMove = (mv) => {
      dragState.current.moved = true;
      const x = Math.max(0, Math.min(window.innerWidth - btnSize, mv.clientX - dragState.current.ox));
      const y = Math.max(0, Math.min(window.innerHeight - btnSize, mv.clientY - dragState.current.oy));
      setFabPos({ x, y });
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      if (!dragState.current.moved) setOpen(true);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [msgs, typing]);
  function respond(q) {
    const t = q.toLowerCase();
    const annual = balances.find((b) => b.type === 'Annual Leave') || {
      entitled: 0,
      used: 0,
    };
    const sick = balances.find((b) => b.type === 'Sick Leave') || {
      entitled: 0,
      used: 0,
    };
    if (t.includes('attention') || t.includes('today') || t.includes('summar'))
      return "Here's what may need your attention: review any pending leave requests, check attendance for late arrivals, and keep the recruitment pipeline moving. Want me to open any of these?";
    if (
      t.includes('leave') ||
      t.includes('balance') ||
      t.includes('holiday') ||
      t.includes('vacation')
    )
      return `You currently have ${annual.entitled - annual.used} days of annual leave remaining (${annual.used} used of ${annual.entitled}) and ${sick.entitled - sick.used} sick days left. Want me to help you start a leave application?`;
    if (
      t.includes('remote') ||
      t.includes('work from home') ||
      t.includes('wfh')
    )
      return 'Per the Remote Work Policy, employees can work remotely up to 2 days per week with manager approval. Core collaboration hours are 11am–4pm.';
    if (t.includes('payslip') || t.includes('salary') || t.includes('pay'))
      return 'You can view and download all your payslips under Payroll.';
    if (t.includes('policy') || t.includes('policies'))
      return 'We have active policies covering Remote Work, Code of Conduct, Leave & Attendance, Information Security, and Expense & Reimbursement. Which would you like to know about?';
    if (
      t.includes('performance') ||
      t.includes('review') ||
      t.includes('appraisal')
    )
      return 'The H1 2026 review cycle is open. Your self-assessment is due 20 June. Check Performance for your goals.';
    if (t.includes('attendance') || t.includes('clock'))
      return 'You can clock in/out under Attendance, and see your recent history there.';
    if (t.includes('hi') || t.includes('hello') || t.includes('hey'))
      return 'Hello! I can answer questions about leave, attendance, payslips, policies, and performance.';
    return "I can help with leave balances, attendance, payslips, policies, and performance reviews. If it's something else, I can connect you with the HR team — just say 'talk to HR'.";
  }
  function send(text) {
    const q = (text ?? input).trim();
    if (!q) return;
    setMsgs((m) => [...m, { from: 'user', text: q }]);
    setInput('');
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      setMsgs((m) => [...m, { from: 'bot', text: respond(q) }]);
    }, 750);
  }
  useEffect(() => {
    if (seed) {
      send(seed);
      clearSeed?.();
    }
  }, [seed]);
  const chips = [
    "What's my leave balance?",
    'Remote work policy?',
    'My latest payslip',
  ];
  return (
    <>
      {!open && (
        <button
          onPointerDown={onFabPointerDown}
          className="fixed flex items-center justify-center z-40"
          style={{
            width: 56,
            height: 56,
            borderRadius: 16,
            background: AG.primary,
            color: '#fff',
            boxShadow: '0 8px 24px rgba(var(--primary-rgb),0.4)',
            border: 'none',
            cursor: fabPos ? 'grab' : 'pointer',
            touchAction: 'none',
            ...(fabPos
              ? { top: fabPos.y, left: fabPos.x }
              : { bottom: 24, right: 24 }),
          }}
        >
          <Bot size={24} />
          <span
            style={{
              position: 'absolute',
              top: -4,
              right: -4,
              width: 16,
              height: 16,
              background: AG.purple,
              borderRadius: 99,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Sparkles size={9} className="text-white" />
          </span>
        </button>
      )}
      {open && (
        <div
          className="fixed bottom-6 right-6 flex flex-col scale-in z-40"
          style={{
            width: 384,
            height: 520,
            maxWidth: 'calc(100vw - 48px)',
            background: '#fff',
            borderRadius: 20,
            border: `1px solid ${AG.n200}`,
            boxShadow: '0 9px 46px rgba(0,0,0,0.25)',
          }}
        >
          <div
            className="flex items-center gap-3"
            style={{
              padding: '12px 16px',
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              background: AG.primaryDarkest,
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 12,
                background: AG.purple,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Bot size={18} className="text-white" />
            </div>
            <div className="flex-1">
              <p style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>
                HR Assistant
              </p>
              <p
                style={{ fontSize: 11, color: AG.primaryLight }}
                className="flex items-center gap-1"
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    background: AG.success,
                    borderRadius: 99,
                  }}
                />
                Online · AI-powered
              </p>
            </div>
            <button
              onClick={() => setOpen(false)}
              style={{ color: AG.primaryLight }}
            >
              <X size={18} />
            </button>
          </div>
          <div
            className="flex-1 overflow-y-auto space-y-3"
            style={{ padding: 16, background: AG.n50 }}
          >
            {msgs.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.from === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  style={{
                    maxWidth: '82%',
                    padding: '10px 14px',
                    borderRadius: 16,
                    fontSize: 14,
                    lineHeight: 1.5,
                    background: m.from === 'user' ? AG.primary : '#fff',
                    color: m.from === 'user' ? '#fff' : AG.n700,
                    border: m.from === 'user' ? 'none' : `1px solid ${AG.n200}`,
                    borderBottomRightRadius: m.from === 'user' ? 6 : 16,
                    borderBottomLeftRadius: m.from === 'bot' ? 6 : 16,
                  }}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {typing && (
              <div className="flex justify-start">
                <div
                  style={{
                    background: '#fff',
                    border: `1px solid ${AG.n200}`,
                    padding: '12px 16px',
                    borderRadius: 16,
                    borderBottomLeftRadius: 6,
                  }}
                >
                  <div className="flex gap-1">
                    <span
                      style={{
                        width: 6,
                        height: 6,
                        background: AG.n300,
                        borderRadius: 99,
                      }}
                      className="animate-bounce"
                    />
                    <span
                      style={{
                        width: 6,
                        height: 6,
                        background: AG.n300,
                        borderRadius: 99,
                        animationDelay: '150ms',
                      }}
                      className="animate-bounce"
                    />
                    <span
                      style={{
                        width: 6,
                        height: 6,
                        background: AG.n300,
                        borderRadius: 99,
                        animationDelay: '300ms',
                      }}
                      className="animate-bounce"
                    />
                  </div>
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>
          {msgs.length <= 1 && (
            <div
              className="flex gap-1.5 flex-wrap"
              style={{ padding: '0 12px 8px' }}
            >
              {chips.map((c) => (
                <button
                  key={c}
                  onClick={() => send(c)}
                  style={{
                    fontSize: 12,
                    background: '#fff',
                    border: `1px solid ${AG.n200}`,
                    color: AG.n600,
                    padding: '6px 10px',
                    borderRadius: 99,
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = AG.primaryLight;
                    e.currentTarget.style.color = AG.primary;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = AG.n200;
                    e.currentTarget.style.color = AG.n600;
                  }}
                >
                  {c}
                </button>
              ))}
            </div>
          )}
          <div
            className="flex gap-2"
            style={{ padding: 12, borderTop: `1px solid ${AG.divider}` }}
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && send()}
              placeholder="Ask anything..."
              className="ag-input"
              style={{ flex: 1, background: AG.n100, border: 'none' }}
            />
            <button
              onClick={() => send()}
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                background: AG.primary,
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                border: 'none',
                cursor: 'pointer',
              }}
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export { ChatWidget };
